import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import CustomInput from "../TextField/TextFeild";
import TimezoneSelect from "../TimeZoneDropDown/TimezoneSelect";
import moment from "moment-timezone";
import { getPollById, removeSelectedPoll, startPoll, updatePoll } from "../../store/slices/pollSlice";
import { useSocket } from "./../../context/SocketContext";

import {
    FaPlus,
    FaImage,
    FaTimes
} from "react-icons/fa";

// Import CKEditor
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import CropperModalAdmin from "./CropperModalAdmin";
import { HUMAN_TIMEZONES } from "../../constants";

const UpdatePoll = () => {
    const dispatch = useDispatch();
    const [step, setStep] = useState(1);
    const { socket } = useSocket();

    const { selectedPoll } = useSelector((state) => state.poll);
    const auth = useSelector((state) => state.auth);
    const roomId = auth?.user?.attributes?.stationChatId;

    const [showModal, setShowModal] = useState(false);
    const [image, setImage] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [description, setDescription] = useState("");
    const [deletedAnswers, setDeletedAnswers] = useState([]);
    const [isImageDeleted, setIsImageDeleted] = useState(false);

    const validationSchema = Yup.object().shape({
        title: Yup.string()
            .required("Title is required")
            .max(200, "Title must not exceed 200 characters"),
    
        answers: Yup.array()
            .of(
                Yup.object().shape({
                    title: Yup.string()
                        .trim()
                        .required("Answer is required"),
                })
            )
            .min(2, "At least two answers are required"),
            rules: Yup.string().max(200, "Title cannot be longer than 200 characters"),
        // STEP 2 VALIDATION — only required in step === 2
        timezone: Yup.string().when("step", {
            is: 2,
            then: (schema) => schema.required("Timezone is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
    
        startDate: Yup.string().when("step", {
            is: 2,
            then: (schema) =>
                schema.when("timezone", {
                    is: (tz) => tz && tz.length > 0,
                    then: (s) => s.required("Start date is required when timezone is selected"),
                    otherwise: (s) => s.notRequired(),
                }),
            otherwise: (schema) => schema.notRequired(),
        }),
    
        startTime: Yup.string().when("step", {
            is: 2,
            then: (schema) =>
                schema.when("timezone", {
                    is: (tz) => tz && tz.length > 0,
                    then: (s) =>
                        s
                            .required("Start time is required when timezone is selected")
                            .test(
                                "is-future-5-min",
                                "Start time must be at least 5 minutes in the future",
                                function (value) {
                                    if (selectedPoll.attributes.isScheduled != 0) return true;
    
                                    const { startDate, timezone } = this.parent;
                                    const tz = HUMAN_TIMEZONES[timezone];
    
                                    if (!startDate || !value || !timezone) return true;
    
                                    const start = moment.tz(
                                        `${startDate} ${value}`,
                                        "YYYY-MM-DD HH:mm",
                                        tz
                                    );
    
                                    const now = moment.tz(tz);
                                    const minutesUntil = start.diff(now, "minutes", true);
    
                                    if (minutesUntil < 5) {
                                        return this.createError({
                                            message: "Start Time must be 5 minutes in advance.",
                                        });
                                    }
    
                                    return true;
                                }
                            ),
                    otherwise: (s) => s.notRequired(),
                }),
            otherwise: (schema) => schema.notRequired(),
        }),
    
        endDate: Yup.string().when("step", {
            is: 2,
            then: (schema) =>
                schema.when("timezone", {
                    is: (tz) => tz && tz.length > 0,
                    then: (s) => s.required("End date is required when timezone is selected"),
                    otherwise: (s) => s.notRequired(),
                }),
            otherwise: (schema) => schema.notRequired(),
        }),
    
        endTime: Yup.string().when("step", {
            is: 2,
            then: (schema) =>
                schema.when(["timezone", "startDate", "startTime"], {
                    is: (tz, sd, st) => tz && sd && st,
                    then: (s) =>
                        s
                            .required("End time is required when timezone is selected")
                            .test(
                                "is-after-start",
                                "End time must be after the start time",
                                function (value) {
                                    let { startDate, startTime, endDate, timezone } = this.parent;
                                    timezone = HUMAN_TIMEZONES[timezone];
    
                                    if (!startDate || !startTime || !endDate || !value || !timezone)
                                        return true;
    
                                    const start = moment.tz(
                                        `${startDate} ${startTime}`,
                                        "YYYY-MM-DD HH:mm",
                                        timezone
                                    );
    
                                    const end = moment.tz(
                                        `${endDate} ${value}`,
                                        "YYYY-MM-DD HH:mm",
                                        timezone
                                    );
    
                                    return end.isAfter(start);
                                }
                            ),
                    otherwise: (s) => s.notRequired(),
                }),
            otherwise: (schema) => schema.notRequired(),
        }),
    });
    


    const convertTo24Hour = (timeStr) => {
        const [time, modifier] = timeStr.split(' ');
        let [hours, minutes] = time.split(':');

        if (modifier === 'PM' && hours !== '12') {
            hours = String(parseInt(hours) + 12);
        } else if (modifier === 'AM' && hours === '12') {
            hours = '00';
        }

        return `${hours}:${minutes}`;
    }

    const handleCrop = (croppedImg) => {
        console.log(croppedImg, 'croppedImg')
        setCroppedImage(croppedImg);
        formik.setFieldValue("image", croppedImg);
        setShowModal(false);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setImage(reader.result);
                setShowModal(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDeleteImage = () => {
        setIsImageDeleted(true);
    };

    const handleUndoDeleteImage = () => {
        setIsImageDeleted(false);
    };

    const removeImage = () => {
        setCroppedImage(null);
        setImage(null);
        formik.setFieldValue("image", "");
        setIsImageDeleted(false);
    };

    const formik = useFormik({
        initialValues: {
            title: selectedPoll?.attributes?.title || "",
            answers: selectedPoll?.relations?.answers?.data?.map((ans) => ({
                id: ans.id,
                title: ans.attributes.title
            })) || [{ id: "", title: "" }, { id: "", title: "" }],
            timezone: selectedPoll?.attributes?.tz || "",
            startDate: selectedPoll?.attributes?.displayData
                ? JSON.parse(selectedPoll.attributes.displayData).startDate
                : "",
            startTime: selectedPoll?.attributes?.displayData
                ? convertTo24Hour(JSON.parse(selectedPoll.attributes.displayData).startTime)
                : "",
            endDate: selectedPoll?.attributes?.displayData
                ? JSON.parse(selectedPoll.attributes.displayData).endDate
                : "",
            endTime: selectedPoll?.attributes?.displayData
                ? convertTo24Hour(JSON.parse(selectedPoll.attributes.displayData).endTime)
                : "",
            image: selectedPoll?.attributes?.image || "",
            description: selectedPoll?.attributes?.description || "",
            state: selectedPoll?.attributes?.state,
        },
        validationSchema,
        enableReinitialize: true,
        validateOnChange: false,
        context: { step },
        onSubmit: async (values, { resetForm }) => {
            // Step 1 → Step 2 (only for unscheduled polls)
            if (step === 1 && selectedPoll.attributes.isScheduled != 0) {
                const errors = await formik.validateForm();
                if (Object.keys(errors).length === 0) {
                    setStep(2);
                }
                return;
            }
        
            // Filter out deleted answers
            const activeAnswers = values.answers
                .map((ans, idx) => ({ ...ans, _index: idx }))
                .filter((ans, idx) => !deletedAnswers.includes(idx))
                .map(({ _index, ...ans }) => ans);
            
            const pollData = {
                ...values,
                answers: activeAnswers,
                image: isImageDeleted ? "" : values.image,
            };
            
            // Step 2 OR already scheduled poll
            dispatch(updatePoll({ data: pollData }))
                .then((data) => {
                    const pollId = data.payload.id;
        
                    socket.emit(`poll.updated`, { poll: data.payload, roomId });
        
                    // If no timezone, start immediately
                    if (!values.timezone) {
                        socket.emit(`poll.started`, { poll: data.payload, roomId });
                        return dispatch(startPoll({ id: pollId }));
                    }
        
                    return true;
                })
                .then(() => {
                    resetForm();
                    setStep(1);
                    onClose();
                });
        }
        
    });

    useEffect(() => {
        if (selectedPoll) {
            const existingImage = selectedPoll.attributes?.image?.trim();
            if (existingImage) {
                setCroppedImage(existingImage);
            }

            // Set existing description if available
            const existingDescription = selectedPoll.attributes?.description || "";
            setDescription(existingDescription);

            // Reset delete states
            setDeletedAnswers([]);
            setIsImageDeleted(false);

            if (selectedPoll.attributes.isScheduled && selectedPoll.attributes.isScheduled != 0) {
                setStep(2);
            } else {
                setStep(1);
            }

        }
    }, [selectedPoll]);

    const handleAnswerChange = (index, value) => {
        const newAnswers = [...formik.values.answers];
        newAnswers[index] = {
            ...newAnswers[index],
            title: value
        };
        formik.setFieldValue("answers", newAnswers);
    };

    const onClose = () => {
        dispatch(removeSelectedPoll());
        setImage(null);
        setCroppedImage(null);
        setDescription("");
        setShowModal(false);
        formik.resetForm();
        setStep(1)
    };

    const setFormikDescription = (data) => {
        setDescription(data);
        formik.setFieldValue("description", data);
    };


    const addAnswer = () => {
        if (formik.values.answers.length < 4) {
            formik.setFieldValue("answers", [...formik.values.answers, { id: "", title: "" }]);
        }
    };

    const handleDeleteAnswer = (index) => {
        setDeletedAnswers([...deletedAnswers, index]);
    };

    const handleUndoDeleteAnswer = (index) => {
        setDeletedAnswers(deletedAnswers.filter((i) => i !== index));
    };

    const removeAnswer = (index) => {
        const newAnswers = formik.values.answers.filter((_, i) => i !== index);
        formik.setFieldValue("answers", newAnswers);
        // Remove from deletedAnswers if it was there
        setDeletedAnswers(deletedAnswers.filter((i) => i !== index).map(i => i > index ? i - 1 : i));
    };

    const hasExistingImage = selectedPoll?.attributes?.image?.trim();
    const hasExistingDescription = selectedPoll?.attributes?.description?.trim();
    return (
        <>
            <Modal
                show={selectedPoll != null}
                size="xl"
                onHide={onClose}
                centered
                backdrop="static"
                keyboard={false}
            >
                <form onSubmit={formik.handleSubmit} className="row m-3">
                    <div className="col-md-12 " style={{ borderBottom: "1px solid grey" }}>
                        {step === 1 && (
                            <>
                                <h4 className="mb-2 d-flex">Update Poll</h4>
                                <p className="text-muted mb-3">
                                    You will be able to alter it and manually start and
                                    stop it.
                                </p>
                            </>
                        )
                        }
                        {step === 2 && (
                            <>
                                <h4 className="mb-2">Start Poll Now or Schedule for Later</h4>
                                <p className="text-muted mb-3">
                                    You can schedule polls to begin at your preferred time and date
                                    in the future.
                                </p>
                            </>
                        )}

                    </div>
                    <div className="col-md-6 shadow-none p-3 m-0" style={{ borderRight: "1px solid grey" }}>
                        {step === 1 && (
                            <>
                                <CustomInput
                                    label="Question"
                                    id="title"
                                    name="title"
                                    value={formik.values.title}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.title && formik.errors.title}
                                    required
                                />

                                {formik.values.answers.map((answer, index) => (
                                    <div key={index} className="d-flex align-items-center mb-3" style={{ position: "relative" }}>
                                        <div className="flex-grow-1" style={{ position: "relative", zIndex: deletedAnswers.includes(index) ? 1 : 2 }}>
                                            <CustomInput
                                                label={`Answer ${index + 1}`}
                                                id={`answers[${index}].title`}
                                                name={`answers[${index}].title`}
                                                value={answer.title}
                                                onChange={(e) => handleAnswerChange(index, e.target.value)}
                                                onBlur={formik.handleBlur}
                                                error={
                                                    !deletedAnswers.includes(index) &&
                                                    formik.errors.answers &&
                                                        formik.touched.answers &&
                                                        typeof formik.errors.answers[index]?.title === "string"
                                                        ? formik.errors.answers[index].title
                                                        : null
                                                }
                                                required
                                                disabled={deletedAnswers.includes(index)}
                                            />
                                        </div>
                                        {formik.values.answers.length > 2 && !deletedAnswers.includes(index) && (
                                            <button
                                                type="button"
                                                className="btn text-danger ms-2"
                                                onClick={() => handleDeleteAnswer(index)}
                                                title="Delete"
                                                style={{ zIndex: 2 }}
                                            >
                                                Delete
                                            </button>
                                        )}
                                        {deletedAnswers.includes(index) && (
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: "100%",
                                                    background: "rgba(0, 0, 0, 0.65)",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    zIndex: 5,
                                                    borderRadius: "4px",
                                                }}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => handleUndoDeleteAnswer(index)}
                                                    className="simpleBtn"
                                                >
                                                    <span>UNDO DELETE</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {formik.values.answers.length < 4 && (
                                    <div className="mb-4">
                                        <button type="button" className="btn btn-outline-primary" onClick={addAnswer}>
                                            Add Answer
                                        </button>
                                    </div>
                                )}


                            </>
                        )}

                        {step === 2 && (
                            <>
                                <TimezoneSelect
                                    label="Timezone"
                                    id="timezone"
                                    name="timezone"
                                    value={formik.values.timezone}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.timezone && formik.errors.timezone}
                                />

                                {selectedPoll.attributes.isScheduled == 1 && (<div className="row">
                                    <div className="col-md-6">
                                        <CustomInput
                                            type="date"
                                            label="Start Date"
                                            name="startDate"
                                            value={formik.values.startDate}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.startDate && formik.errors.startDate}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <CustomInput
                                            type="time"
                                            label="Start Time"
                                            name="startTime"
                                            value={formik.values.startTime}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.startTime && formik.errors.startTime}
                                        />
                                    </div>
                                </div>)}

                                <div className="row">
                                    <div className="col-md-6">
                                        <CustomInput
                                            type="date"
                                            label="End Date"
                                            name="endDate"
                                            value={formik.values.endDate}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.endDate && formik.errors.endDate}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <CustomInput
                                            type="time"
                                            label="End Time"
                                            name="endTime"
                                            value={formik.values.endTime}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.endTime && formik.errors.endTime}
                                        />
                                    </div>
                                </div>


                            </>
                        )}
                    </div>
                    <div className="col-md-6 border-right p-3">
                        <h6 className="mb-3 text-decoration-underline text-center">Optional Information</h6>
                        <div className="col-md-12">
                            {/* Image Upload Section */}
                            <div className="d-flex flex-column align-items-center">
                                <div className="position-relative mb-3 w-100" style={{ position: "relative" }}>
                                    {croppedImage && !isImageDeleted ? (
                                        <div className="d-flex align-items-center">
                                            <img
                                                src={croppedImage}
                                                alt="Contest"
                                                className="img-fluid"
                                                style={{
                                                    width: "90%",
                                                    height: "120px",
                                                    objectFit: "contain",
                                                    border: "2px solid #ddd",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#f8f9fa"
                                                }}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    // If image fails to load, show upload area
                                                    setCroppedImage(null);
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleDeleteImage}
                                                style={{ border: "0px", outline: "0px", cursor: "pointer", background: "none", marginLeft: "10px" }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    ) : croppedImage && isImageDeleted ? (
                                        <div className="position-relative">
                                            <img
                                                src={croppedImage}
                                                alt="Contest"
                                                className="img-fluid"
                                                style={{
                                                    width: "100%",
                                                    height: "120px",
                                                    objectFit: "contain",
                                                    border: "2px solid #ddd",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#f8f9fa"
                                                }}
                                            />
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: "100%",
                                                    background: "rgba(0, 0, 0, 0.65)",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    zIndex: 5,
                                                    borderRadius: "8px",
                                                }}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={handleUndoDeleteImage}
                                                    className="simpleBtn"
                                                >
                                                    <span>UNDO DELETE</span>
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            className="d-flex flex-column align-items-center justify-content-center position-relative"
                                            style={{
                                                width: "100%",
                                                height: "100px",
                                                backgroundColor: "#f8f9fa",
                                                border: "2px dashed #ddd",
                                                borderRadius: "8px",
                                                cursor: "pointer"
                                            }}
                                            onClick={() => document.getElementById('profileUpload').click()}
                                        >
                                            <FaImage size={40} color="#aaa" className="mb-2" />
                                            <small className="text-muted">
                                                {hasExistingImage ? "Click to change image" : "Click to upload image"}
                                            </small>
                                        </div>
                                    )}
                                    <label
                                        htmlFor="profileUpload"
                                        className="btn btn-sm btn-primary position-absolute"
                                        style={{
                                            bottom: "-15px",
                                            right: "50%",
                                            transform: "translateX(50%)",
                                            borderRadius: "50%",
                                            width: "30px",
                                            height: "30px",
                                            padding: "0",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                                        }}
                                    >
                                        <FaPlus size={12} />
                                    </label>
                                    <input
                                        type="file"
                                        id="profileUpload"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        style={{ display: "none" }}
                                    />
                                </div>
                            </div>

                            {/* CKEditor Section */}
                            <div className="my-4">
                                <label htmlFor="poll-description" className="form-label">
                                    Additional Information <small className="text-muted">(Optional)</small>
                                </label>
                                <div className="ckeditor-container" style={{ minHeight: "100px" }}>
                                    <CKEditor
                                        editor={ClassicEditor}
                                        data={description}
                                        onChange={(event, editor) => {
                                            const data = editor.getData();
                                            setFormikDescription(data);
                                        }}
                                        config={{
                                            placeholder: "Add a detailed description for your contest...",
                                            toolbar: {
                                                items: [
                                                    'heading', '|',
                                                    'bold', 'italic', 'underline', 'strikethrough', '|',
                                                    'fontColor', 'fontBackgroundColor', '|',
                                                    'bulletedList', 'numberedList', '|',
                                                    'alignment', '|',
                                                    'link', '|',
                                                    'undo', 'redo'
                                                ]
                                            },
                                            language: 'en'
                                        }}
                                        onReady={(editor) => {
                                            editor.editing.view.change((writer) => {
                                                writer.setStyle(
                                                    'height',
                                                    '100px',
                                                    editor.editing.view.document.getRoot()
                                                );
                                            });
                                        }}
                                    />
                                </div>
                                {description && (
                                    <div className="mt-2">
                                        <small className="text-muted">
                                            Character count: {description.replace(/<[^>]*>/g, '').length}
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-md-12 ">
                        {step === 1 && (
                            <div className="d-flex justify-content-end mt-3 modalBtnContainer">
                                <button type="button" className="btn btn-outline-secondary me-2" onClick={onClose}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-success">
                                    Update Poll
                                </button>
                            </div>
                        )
                        }
                        {step === 2 && (
                            <div className="d-flex justify-content-end mt-3 gap-2 ">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setStep(1)}
                                >
                                    Back
                                </button>

                                <button
                                    type="submit"
                                    className="btn btn-success"
                                    disabled={
                                        !formik.values.startDate &&
                                        !formik.values.startTime &&
                                        !formik.values.endDate &&
                                        !formik.values.endTime &&
                                        !formik.values.timezone
                                    }
                                >
                                    {formik.isSubmitting ? "Scheduling..." : "Schedule Poll"}
                                </button>
                            </div>
                        )}

                    </div>
                </form>
            </Modal>
            {showModal && (
                <CropperModalAdmin
                    show={showModal}
                    onClose={() => setShowModal(false)}
                    image={image}
                    heading={'Poll Image'}
                    aspectRatio={1242 / 180}
                    onCrop={handleCrop}
                />
            )}
        </>
    );
};

export default UpdatePoll;